import { useEffect, useState } from "react";
import type { Answers } from "@/schema/quizSchema";
import { deriveProfile } from "./profile";

const STEP_MS = 1250;

const ROWS = [
  "Counting the products in daily contact",
  "Cross-checking his brand against the disclosure loophole",
  "Calculating days used in his development window",
  "Grading his exposure level",
];

/**
 * The building surface, on the SAME approved-prototype tick chassis as the
 * main B.O. quiz's loader (`.qk-manly-loader` / `.qk-manly-lrow` in
 * manly/report.tokens.css, injected quiz-wide via QuizDefinition.styles):
 * rows light up in turn, a gold pulsing ring while a row is active, then the
 * tick fills gold with a check once done. What stays hormone-specific is the
 * copy and the trailing detail each finished row reveals: real counts from
 * the profile rather than theatre, which is what makes it read as analysis.
 *
 * Owns its timing per the renderBuilding contract: every row timer AND the
 * final onDone timer are scheduled up front at fixed absolute offsets
 * (chaining off committed state stalls under a single fake-timer advance).
 */
export function BuildingRows({ answers, onDone }: { answers: Answers; onDone: () => void }) {
  // How many rows have completed. 0 = first row active; ROWS.length = all done.
  const [active, setActive] = useState(0);
  const profile = deriveProfile(answers);

  useEffect(() => {
    const timers = ROWS.map((_, i) =>
      window.setTimeout(() => setActive(i + 1), (i + 1) * STEP_MS),
    );
    // A short tail after the last tick before advancing, matching the sibling.
    timers.push(window.setTimeout(onDone, ROWS.length * STEP_MS + 600));
    return () => timers.forEach((t) => clearTimeout(t));
  }, [onDone]);

  const detail = [
    `${profile.exposureScore === 0 ? "no" : profile.exposureScore} exposure ${
      profile.exposureScore === 1 ? "point" : "points"
    }`,
    profile.brandLabel,
    `${profile.window.used} of ${profile.window.total}`,
    profile.exposure,
  ];

  return (
    <div className="qk-manly">
      <div className="qk-manly-loader" role="status" aria-live="polite">
        <h2>Building his Exposure Profile…</h2>
        <p className="qk-manly-loader-lead">
          Hold tight while we map your answers against what his products are allowed to leave off
          the label.
        </p>

        <ol className="qk-manly-loader-rows">
          {ROWS.map((row, i) => {
            const state = active > i ? "done" : active === i ? "active" : "pending";
            return (
              <li key={row} className={`qk-manly-lrow is-${state}`}>
                <span className="qk-manly-tick" aria-hidden="true">{state === "done" ? "✓" : ""}</span>
                {/* Detail sits on its own line UNDER the label, and that line is
                    always present (nbsp placeholder until done), so revealing
                    the count never reflows the label or shifts rows below. */}
                <span className="qk-manly-lrow-text">
                  <span>{row}</span>
                  <strong>{state === "done" ? detail[i] : " "}</strong>
                </span>
              </li>
            );
          })}
        </ol>

        <p className="qk-manly-loader-foot">His profile is almost ready</p>
      </div>
    </div>
  );
}
